import type { FC, ReactNode, MutableRefObject } from 'react';
import React, { Fragment, useEffect, useCallback, memo, useRef, useMemo, useContext } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import isEqual from 'lodash/isEqual';
import { useFragment } from 'react-relay';

import { announce } from '@atlaskit/pragmatic-drag-and-drop-live-region';
import { MenuListItem } from '@atlaskit/side-nav-items/menu-list-item';

import { ApolloErrorInterop } from '@confluence/apollo-relay-interop-utils/entry-points/ApolloErrorInterop.ts';
import { useRenderServerPlaceholder } from '@confluence/ssr-utilities/entry-points/useRenderServerPlaceholder.ts';
import { expVal, expValEquals } from '@confluence/feature-experiments';
import { LazyEmojiComponentLoader } from '@confluence/emoji-title/entry-points/EmojiComponent.tsx';
import {
	ReloadType,
	QuickReloadSubscriber,
} from '@confluence/quick-reload/entry-points/subscription.ts';
import type {
	DragPreview,
	TreeDestinationPosition,
	TreeSourcePosition,
	RenderItem,
	RenderItemParams,
} from '@confluence/tree/entry-points/tree-types.ts';
import { LoadableLazy } from '@confluence/loadable';
import { usePageState } from '@confluence/page-context';
import type { PageTreeStateUpdater } from '@confluence/page-tree-refresh-state-container';
import {
	useMessageSubscriber,
	Message,
} from '@confluence/page-tree-refresh-state-container/entry-points/messageBus.ts';
import { useIsMultiSelectActive } from '@confluence/content-tree-actions/entry-points/useIsMultiSelectActive.ts';
import { SPACE_OVERVIEW } from '@confluence/named-routes/entry-points/routes.ts';
// eslint-disable-next-line no-restricted-imports -- deprecated - use RouteState instead
import { RoutesContext } from '@confluence/route-manager/entry-points/RoutesContext.ts';
import type { FlagsStateContainer } from '@confluence/flags/entry-points/FlagsStateContainer.ts';
import { PageTreeSmartLinkDropMonitor } from '@confluence/smart-link-droppable/entry-points/PageTreeSmartLinkDropMonitor.tsx';
import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';
import { useIsSmartFoldersEnabled } from '@confluence/smart-folder/entry-points/useIsSmartFoldersEnabled.ts';

import { EmojiWrapper } from './PageTreeItem.tsx';
import type { ContentTreeItem } from './data-transformers.ts';
import { applyManagedFolderRestrictions, isManagedFolder } from './data-transformers.ts';
import { findSyntheticItemParentId, useAutoMoveSyntheticItem } from './autoMoveExpansion.ts';
import type { MovePageParams } from './useMovePageHandler.ts';
import type { PageTreeState } from './usePageTreeState.ts';
import {
	findParentId,
	usePageTreeState,
	PageTreeStateContainer,
	useContentIdsWithLoadedChildren,
} from './usePageTreeState.ts';
import { VIRTUAL_LOCAL_ROOT_ID, VIRTUAL_ROOT_ID } from './virtualRootIds.js';
import { PageTreeComponent } from './PageTreeComponent.tsx';
import {
	usePageTreeData,
	generateQueryArgs,
	useFetchPages,
	useLoadPagesCallback,
} from './usePageTreeData.ts';
import { useMovePage } from './useMovePage.tsx';
import { useMovePageHandler } from './useMovePageHandler.ts';
import { renderItemEmptyState } from './renderItemEmptyState.tsx';
import type { GraphQLPageStatus } from './pageTreeStatuses.ts';
import type { PageTreeQuery as PageTreeQueryResponse } from './queries/__types__/PageTreeQuery.ts';
import type { PageTreeRootLevelPagesQuery as PageTreeRootLevelPagesResponse } from './queries/__types__/PageTreeRootLevelPagesQuery.ts';
import { BulkActionsMenu } from './bulk-actions/BulkActionsMenu.tsx';
import { useNestedSelect } from './bulk-actions/useNestedSelect.ts';
import { useOneOnOnePagesAnalytics } from './useOneOnOnePagesAnalytics.ts';
import { PageTreeEmptyComponentLoader } from './PageTreeEmptyComponentLoader.tsx';
import { FooterCreateButtons } from './FooterCreateButtonsLoader.tsx';
import { PageTreeInitialDataFragment } from './PageTreeInitialData.fragment.ts';
import type { PageTreeInitialData_query$key } from './__generated__/PageTreeInitialData_query.graphql.ts';
const noop = () => {};

const PageTreeErrorComponentLoader = LoadableLazy({
	loader: async () =>
		(
			await import(
				/* webpackChunkName: "loadable-PageTreeErrorComponent" */ './PageTreeErrorComponent.tsx'
			)
		).PageTreeErrorComponent,
});

const StopLinkClickSSRInlineScript = () => {
	const nonceValue = window.__SSR_NONCE_VALUE__;
	return (
		<script
			{...(nonceValue !== undefined ? { nonce: nonceValue } : {})}
			/* eslint-disable-next-line react/no-danger -- Inline script for SSR chevron click handling, content is static */
			dangerouslySetInnerHTML={{
				__html: `(function(){
  const rightChevrons = document.querySelectorAll('[data-testid="chevron-right"]');
  const downChevrons = document.querySelectorAll('[data-testid="chevron-down"]');
  const allChevrons = [...rightChevrons, ...downChevrons];
  for(let i = 0; i <= allChevrons.length; i++){
    allChevrons[i] ? allChevrons[i].onclick= function(e) {
      e.preventDefault();
      e.stopPropagation();
    }: null;
  }
})()`,
			}}
		/>
	);
};

/**
 * Conditionally wraps `children` in a `MenuListItem` (`role="listitem"`).
 *
 * In the space sidebar the page tree renders inside `ExpandableMenuItemContent`, which is a
 * `role="list"`, so the tree's `<ul>`, the create buttons and the bulk-action menu all have to be
 * list items (CONFCLOUD-85073). Two reasons a callsite may opt out: the move dialog renders the same
 * tree outside any list, where a stray `role="listitem"` would be a violation of its own, and the
 * corrected markup is rolling out behind the `confluence_sidenav_content_list_semantics` experiment.
 */
const OptionalMenuListItem: FC<{ asListItem: boolean; children: ReactNode }> = ({
	asListItem,
	children,
}) => (asListItem ? <MenuListItem>{children}</MenuListItem> : <Fragment>{children}</Fragment>);

export const PageTreeCoordinatorComponent: FC<PageTreeCoordinatorProps> = memo(
	({
		currentPageId,
		isPeekingFromBlogs,
		isSuperAdmin,
		spaceKey,
		statuses,
		expandSpaceHomepage = false,
		onInitialLoadComplete = noop,
		onLoadMore = noop,
		onDragStart = noop,
		onPageMove = noop,
		setIsPageTreeLoading = noop,
		onMoveInvalid,
		renderItem,
		forceFetchCurrentPage = false,
		forceFetchedCallback,
		optimisticDeleteId,
		clearDeleteId,
		highlightPages = [],
		isDraggable,
		syntheticItem,
		detached = false,
		syntheticItemIsDraft,
		getIsDraggingItem,
		setIsDraggingItem,
		dragPreview,
		renderResourcedEmoji,
		setContentTreeSize,
		setIsPageTreeEmpty,
		isMovePageTree,
		moveToParentId,
		pageTreeStateUpdatesContainer,
		isCreateContentRoute = false,
		flags,
	}) => {
		const prevPropsRef: PrevPropsRef = useRef({
			previousPageId: '',
			previousSpaceKey: '',
			previousHighlightedPages: [],
			previousForceFetchCurrentPage: false,
			loadingUseEffect: false,
		});
		const isMountedRef = useRef(true);
		const intl = useIntl();
		const [state, actions] = usePageTreeState();
		const [isMultiSelectActive] = useIsMultiSelectActive();
		const {
			pages,
			space,
			rootId,
			isTreeTruncatedPrevious,
			isTreeTruncatedFollowing,
			isPageTreeLoading,
		} = state as PageTreeState;
		const renderServerPlaceholder = useRenderServerPlaceholder();
		const { match } = useContext(RoutesContext);
		const [contentIdsWithLoadedChildren] = useContentIdsWithLoadedChildren();
		const selectContentAndDescendants = useNestedSelect();

		// When moving the space homepage via the move dialog, the homepage appears to have no children
		// because the synthetic item replaces it as the root. Suppress the empty state in this case
		// to avoid incorrectly showing "No content in this space". See DISCO-1827.
		const isMovingHomepage = isMovePageTree && syntheticItem?.id === space?.homepage?.id;
		const isPageTreeEmpty = Boolean(
			!isPageTreeLoading &&
			((space?.homepage?.id &&
				rootId == space?.homepage?.id &&
				pages[rootId]?.children?.length == 0 &&
				!isMovingHomepage) || // rootId is homepage and homepage has no children. empty state is [VIRTUAL_ROOT_ID, VIRTUAL_LOCAL_ROOT_ID, homepage, and/or any orphan pages]
				(!space?.homepage?.id && Object.keys(pages).length <= 2) || // there is no homepage so tree starts at virtual root. empty state is [VIRTUAL_ROOT_ID, VIRTUAL_LOCAL_ROOT_ID]
				(space?.homepage?.id && rootId != space?.homepage?.id && Object.keys(pages).length < 3)), // there is a homepage, but we show from virtual root (i.e. on archived pages). empty state is [VIRTUAL_ROOT_ID, VIRTUAL_LOCAL_ROOT_ID]
		);

		useEffect(() => {
			setContentTreeSize && setContentTreeSize(Object.keys(pages).length);
		}, [pages, setContentTreeSize]);

		useEffect(() => {
			setIsPageTreeEmpty && setIsPageTreeEmpty(isPageTreeEmpty);
		}, [isPageTreeEmpty, setIsPageTreeEmpty]);

		const onLoadMoreFn = useCallback(
			(pageCount: any, direction: any) => {
				if (onLoadMore) {
					onLoadMore(pageCount, space?.id, direction);
				}
			},
			[onLoadMore, space],
		);

		const { loadPreviousPages, loadFollowingPages, reloadChildren, queryChildren } =
			usePageTreeData(spaceKey, isSuperAdmin, statuses, onLoadMoreFn);

		// Note: loading is never true when using refetch, so we are manually updating isPageTreeLoading in handleData
		const { refetch: loadPages } = useFetchPages(
			'1',
			true,
			false,
			detached,
			spaceKey,
			isSuperAdmin,
		);

		const { refetch: forceFetchPages } = useFetchPages(
			'1',
			true,
			true,
			detached,
			spaceKey,
			isSuperAdmin,
		);

		const { refetch: fetchRoot } = useFetchPages(
			undefined,
			true,
			true,
			detached,
			spaceKey,
			isSuperAdmin,
		);
		const updateData = useLoadPagesCallback(
			spaceKey,
			expandSpaceHomepage,
			syntheticItem,
			isPeekingFromBlogs,
			isSuperAdmin,
			statuses,
			syntheticItemIsDraft,
			pageTreeStateUpdatesContainer,
		);

		const handleUnexpectedPtpageNull = useCallback(
			(data) => {
				// Root level ptpage query returning null when the space homepage exists is unexpected and indicates
				// there was a backend issue. It is not feasible to throw an error for this case from the backend. This
				// is a frontend workaround for now to prevent the empty state from being incorrectly shown for overview.
				if (
					data?.ptpage === null &&
					data?.currentPage !== null &&
					data?.space?.homepage !== null &&
					match?.name === SPACE_OVERVIEW.name
				) {
					actions.handleLoadingError(
						new ApolloErrorInterop({
							errorMessage: 'ptpage query unexpectedly returned null',
						}),
					);
				}
			},
			[actions, match?.name],
		);

		const handleData = useCallback(
			async (response: any) => {
				const { data, error } = response;
				handleUnexpectedPtpageNull(data);
				if (error) {
					// handle errors, ignoring undefined refetch error after unmount
					if (isMountedRef.current) {
						actions.handleLoadingError(error);
					}
					return data;
				} else {
					actions.setIsPageTreeLoading(false);
					updateData(data);
					if (forceFetchCurrentPage && forceFetchedCallback) {
						forceFetchedCallback(state);
					}
					return data;
				}
			},
			[
				updateData,
				actions,
				forceFetchCurrentPage,
				forceFetchedCallback,
				state,
				handleUnexpectedPtpageNull,
			],
		);

		const movePage = useMovePage({
			onMoveInvalid,
			syntheticItem,
			onPageMove,
			queryChildren,
		});

		// Repositions a page via one of the four move actions, reusing the drag-and-drop `movePage`
		// path (validation, optimistic update, staging), then announces the result to screen readers.
		const onMovePageAction = useCallback(
			(contentId: string, action: 'up' | 'down' | 'out' | 'into' | 'top' | 'bottom') => {
				const { pages: currentPages } = actions.getState();
				const item = currentPages[contentId];
				// The move dialog injects the moving page as a synthetic item whose parentId is
				// stripped, so derive the parent from whichever page lists it as a child.
				const parentId = item ? findParentId(currentPages, contentId) : undefined;
				if (!parentId || !currentPages[parentId]) {
					return;
				}
				const siblings = currentPages[parentId].children ?? [];
				const index = siblings.indexOf(contentId);
				if (index === -1) {
					return;
				}
				const source = { parentId, index };

				if (action === 'up' || action === 'down' || action === 'top' || action === 'bottom') {
					const lastIndex = siblings.length - 1;
					const destinationIndex =
						action === 'up'
							? index - 1
							: action === 'down'
								? index + 1
								: action === 'top'
									? 0
									: lastIndex;
					if (destinationIndex < 0 || destinationIndex > lastIndex || destinationIndex === index) {
						return;
					}
					const reason =
						action === 'up'
							? 'keyboard-reorder-up'
							: action === 'down'
								? 'keyboard-reorder-down'
								: action === 'top'
									? 'keyboard-move-to-top'
									: 'keyboard-move-to-bottom';
					movePage(source, { parentId, index: destinationIndex }, reason);
					const { pages: updatedPages } = actions.getState();
					const updatedSiblings = updatedPages[parentId]?.children ?? [];
					const newIndex = updatedSiblings.indexOf(contentId);
					if (newIndex !== -1 && newIndex !== index) {
						announce(
							intl.formatMessage(i18nReorder.reorderAnnouncement, {
								title: item.data.title,
								position: newIndex + 1,
								total: updatedSiblings.length,
							}),
						);
					}
					return;
				}

				if (action === 'out') {
					// Outdent: become a sibling immediately after the current parent. Only valid when
					// the grandparent is a real content node — never a virtual root — otherwise a
					// top-level page would be moved above the space root and disappear.
					const grandparentId = findParentId(currentPages, parentId);
					if (
						!grandparentId ||
						grandparentId === VIRTUAL_ROOT_ID ||
						grandparentId === VIRTUAL_LOCAL_ROOT_ID ||
						!currentPages[grandparentId]
					) {
						return;
					}
					const parentIndex = (currentPages[grandparentId].children ?? []).indexOf(parentId);
					if (parentIndex === -1) {
						return;
					}
					movePage(
						source,
						{ parentId: grandparentId, index: parentIndex + 1 },
						'keyboard-move-out',
					);
					announce(intl.formatMessage(i18nReorder.moveOutAnnouncement, { title: item.data.title }));
					return;
				}

				// Indent under the previous sibling (as its last child).
				if (index === 0) {
					return;
				}
				const previousSiblingId = siblings[index - 1];
				const previousSibling = currentPages[previousSiblingId];
				if (!previousSibling) {
					return;
				}
				movePage(source, { parentId: previousSiblingId, index: undefined }, 'keyboard-move-into');
				announce(
					intl.formatMessage(i18nReorder.moveIntoAnnouncement, {
						title: item.data.title,
						target: previousSibling.data.title,
					}),
				);
			},
			[actions, movePage, intl],
		);

		const handleRetry = useCallback(() => {
			actions.clearError();
			actions.setIsPageTreeLoading(true);
			const { options } = generateQueryArgs(
				currentPageId,
				true,
				false,
				detached,
				spaceKey,
				isSuperAdmin,
			);
			forceFetchPages(options.variables)
				.then((response) => handleData(response))
				.catch((error) => handleData({ error }));
		}, [actions, currentPageId, detached, spaceKey, isSuperAdmin, forceFetchPages, handleData]);

		const firePropCallbacks = useCallback(() => {
			setIsPageTreeLoading(isPageTreeLoading);
		}, [setIsPageTreeLoading, isPageTreeLoading]);

		useEffect(() => {
			// Now that Create Content route lives under the same <MainLayout/> as the other view content routes,
			// the content tree does not get remounted again when going from create -> view content route.
			// Exiting out of this useEffect while on create route will allow the fetching of data with the
			// correct draftId upon redirection.
			if (isCreateContentRoute) {
				return;
			}
			const {
				previousPageId,
				previousSpaceKey,
				previousHighlightedPages,
				previousForceFetchCurrentPage,
			} = prevPropsRef.current;

			const { options } = generateQueryArgs(
				currentPageId,
				true,
				false,
				detached,
				spaceKey,
				isSuperAdmin,
			);
			const fetchPages = currentPageId ? loadPages : fetchRoot;

			const fetchData = async () => {
				const fetchAndHandle = () =>
					forceFetchPages(options.variables)
						.then((response) => handleData(response))
						.catch((error) => handleData({ error }));

				//toDo: this can be massively simplified
				if (
					//have currentPageId
					currentPageId &&
					(forceFetchCurrentPage || previousPageId !== currentPageId)
				) {
					const currentPageInMemory = pages[currentPageId];
					const hasHydratedCurrentPageInMemory =
						currentPageInMemory && !currentPageInMemory.data.isOptimistic;
					if (forceFetchCurrentPage && !previousForceFetchCurrentPage) {
						return fetchAndHandle();
					} else if (highlightPages.length && !isEqual(previousHighlightedPages, highlightPages)) {
						// Load and/or expand pages we should highlight
						const highlightPage = pages[highlightPages[0]];
						if (!highlightPage) {
							//this happens when unarchiving pages, and MissionControl CI tests require a refresh/needs a loading state to show
							return fetchAndHandle();
						} else if (highlightPage) {
							return actions.expandPage(highlightPage, queryChildren);
						}
					} else if (
						!currentPageInMemory ||
						currentPageInMemory.data.isOptimistic || // Ensures we pull the full content data from the server, replacing the optimistic data
						(spaceKey !== previousSpaceKey && previousSpaceKey !== '') //if we loaded from persistence the previous spacekey will be ""
					) {
						const isCrossSpaceTransition = spaceKey !== previousSpaceKey && previousSpaceKey !== '';
						if (
							!isCrossSpaceTransition &&
							hasHydratedCurrentPageInMemory &&
							expVal('confluence_optimize_overview_transition_nav', 'isEnabled', false)
						) {
							firePropCallbacks();
							return;
						}
						return fetchAndHandle();
					} else {
						if (!window.__SSR_RENDERED__ && previousPageId === '') {
							const isCrossSpaceTransition =
								spaceKey !== previousSpaceKey && previousSpaceKey !== '';
							if (
								!isCrossSpaceTransition &&
								hasHydratedCurrentPageInMemory &&
								expVal('confluence_optimize_overview_transition_nav', 'isEnabled', false)
							) {
								firePropCallbacks();
								return;
							}
							//only forceFetch on transitions from other views. If local ref isn't set we must be on a transition
							return fetchAndHandle(); //each mount we have to call the server unless we SSR'd
						} else if (
							currentPageId !== space?.homepage?.id && //don't get children for the overview if we're in a paginated space
							previousPageId !== currentPageId &&
							currentPageInMemory &&
							(currentPageInMemory.hasChildren || currentPageInMemory.data.type === 'folder')
						) {
							// This expansion only exists to make the current page's children visible in the
							// tree after arriving from a link, search, or the folder view.
							// - suppressAnalytics: avoids double-firing the "pageTree expanded" event when the
							//   user clicked a folder to navigate to it; the click handler already fired it.
							// - respectUserCollapse: a folder click both collapses and navigates, so without
							//   this the collapse would be undone immediately by the route change.
							return actions.expandPage(currentPageInMemory, queryChildren, {
								suppressAnalytics: true,
								respectUserCollapse: true,
							});
						}
					}
				}

				if (!currentPageId && spaceKey !== previousSpaceKey) {
					//no currentPageId
					return fetchPages(options.variables)
						.then((response) => {
							return handleData(response);
						})
						.catch((error) => handleData({ error })); //when we change spaces in the movePageDialog or peek from blogs
				}
			};

			if (optimisticDeleteId) {
				actions.optimisticDeleteContent(optimisticDeleteId, clearDeleteId);
			}

			if (
				!prevPropsRef.current.loadingUseEffect ||
				(forceFetchCurrentPage && !previousForceFetchCurrentPage)
			) {
				prevPropsRef.current.loadingUseEffect = true;
				fetchData()
					.then((data) => {
						if (data) {
							firePropCallbacks();
						}
						prevPropsRef.current.loadingUseEffect = false;
					})
					.catch((error) => handleData({ error }));
			}
			//we useRef so that we don't trigger another re-render like we would if we used State
			prevPropsRef.current.previousPageId = currentPageId;
			prevPropsRef.current.previousSpaceKey = spaceKey;
			prevPropsRef.current.previousHighlightedPages = highlightPages;
			prevPropsRef.current.previousForceFetchCurrentPage = forceFetchCurrentPage;

			//persist tree between route changes in JS memory
			actions.updatePersistedState();
		}, [
			actions,
			currentPageId,
			actions.expandPage,
			forceFetchCurrentPage,
			forceFetchPages,
			highlightPages,
			loadPages,
			fetchRoot,
			pages,
			spaceKey,
			forceFetchedCallback,
			queryChildren,
			firePropCallbacks,
			detached,
			handleData,
			isSuperAdmin,
			isPeekingFromBlogs,
			state,
			syntheticItem,
			syntheticItemIsDraft,
			isCreateContentRoute,
			clearDeleteId,
			optimisticDeleteId,
			space?.homepage?.id,
		]);

		// TODO: Delete isMountedRef code after blog tree view has been removed
		// This is needed to prevent undefined refetch error after unmount
		useEffect(() => {
			return () => {
				isMountedRef.current = false;
			};
		}, []);

		useMessageSubscriber(
			Message.ContentCreated,
			useCallback(
				(newContent) => {
					actions.optimisticInsertContent(newContent);
				},
				[actions],
			),
		);

		// Removes a whole batch in one pass. `optimisticDeleteContent` re-reads the store on every
		// call, so sibling removals — and the parent's `hasChildren` — stay correct across the loop,
		// and all of them coalesce into a single render. An id that isn't currently loaded is a
		// no-op. No clear callback: unlike the `optimisticDeleteId` prop above there is no slot to
		// release, which is why a batch can come through here and cannot through that single field.
		useMessageSubscriber(
			Message.ContentRemoved,
			useCallback(
				({ contentIds }) => {
					contentIds.forEach((contentId) => actions.optimisticDeleteContent(contentId, undefined));
				},
				[actions],
			),
		);

		useMessageSubscriber(
			Message.SmartFolderSyncStateChanged,
			useCallback(
				({ contentId, subType }) => {
					const { pages: latestPages } = actions.getState();
					if (!latestPages[contentId]) {
						return;
					}

					actions.updateSinglePage(contentId, { subType });

					// When a managed folder is unsynced, propagate the subType change to all loaded descendants
					if (!subType) {
						const clearManagedDescendants = (parentId: string) => {
							const { pages: currentPages } = actions.getState();
							const childIds = currentPages[parentId]?.children ?? [];
							for (const childId of childIds) {
								const child = currentPages[childId];
								if (child?.data?.subType === 'managed') {
									actions.updateSinglePage(childId, { subType: null });
									clearManagedDescendants(childId);
								}
							}
						};
						clearManagedDescendants(contentId);
					}

					void actions.refreshChildren(contentId, queryChildren);
				},
				[actions, queryChildren],
			),
		);

		useOneOnOnePagesAnalytics(pages);

		const { movePage: movePageHandler } = useMovePageHandler();

		const memoizedTree = useMemo(
			() => ({
				rootId,
				items: applyManagedFolderRestrictions(pages),
			}),
			[rootId, pages],
		);

		const onClick = useCallback(
			//toDo only pass this in if we're in the movePageDialog
			async (item: ContentTreeItem) => {
				if (!syntheticItem) {
					return;
				}

				const sourceId = syntheticItem.id;
				if (item.id === sourceId) {
					return;
				}

				// The reference to `pages` changes fairly frequently.
				// Given that `onClick` only needs `pages` when it is called, we can
				// retrieve the latest `pages` from the store when it is needed.
				// By doing this we can keep the reference to `onClick` more stable
				const { pages: latestPages } = actions.getState();

				const sourceParentId = findSyntheticItemParentId(latestPages, sourceId);

				if (!sourceParentId) {
					return;
				}

				const source = {
					parentId: sourceParentId,
					index: latestPages[sourceParentId]?.children?.indexOf(sourceId),
				};
				const destination = { parentId: item.id };
				source.index !== -1 && movePage(source, destination);
			},
			[syntheticItem, actions, movePage],
		);

		useAutoMoveSyntheticItem({
			isMovePageTree,
			moveToParentId,
			syntheticItem,
			actions,
			queryChildren,
			isMountedRef,
			pages,
		});

		const expandPage = useCallback(
			(page: ContentTreeItem) => {
				const wereChildrenPreviouslyLoaded = contentIdsWithLoadedChildren?.has(page.id) ?? false;

				void actions.expandPage(page, queryChildren).then(() => {
					// We only want to select children that were not previously selected and the parent is selected
					if (isMultiSelectActive && !wereChildrenPreviouslyLoaded && flags) {
						selectContentAndDescendants(page.id, false, flags);
					}
				});
			},
			[
				contentIdsWithLoadedChildren,
				actions,
				queryChildren,
				isMultiSelectActive,
				selectContentAndDescendants,
				flags,
			],
		);

		const isTreeDraggable = useCallback(
			(item: ContentTreeItem) => {
				// Items inside a managed folder cannot be dragged out.
				// Read pages from the store at call time to avoid recreating this callback
				// on every page-state update (same pattern as onClick, see lines above).
				const { pages: latestPages } = actions.getState();
				const parent = item.parentId ? latestPages[item.parentId] : null;
				if (parent && isManagedFolder(parent.data)) {
					return false;
				}
				return isDraggable(item, { space });
			},
			[space, isDraggable, actions],
		);

		const { isSmartFoldersEnabled } = useIsSmartFoldersEnabled(spaceKey);

		const onDragBlocked = useCallback(
			(item: ContentTreeItem) => {
				const { pages: latestPages } = actions.getState();
				const parent = item.parentId ? latestPages[item.parentId] : null;
				if (parent && isManagedFolder(parent.data)) {
					onMoveInvalid?.({ reason: 'INVALID_MOVE_LOCATION_MANAGED_FOLDER' });
				}
			},
			[actions, onMoveInvalid],
		);

		const renderPageTreeItem: RenderItem<ContentTreeItem> = useCallback(
			(params) => {
				const spaceHomePageId = space?.homepage?.id;
				const displayEmoji = params?.item?.data?.emoji;
				const contentType = params?.item?.data?.type;
				const isSmartLinkEmbed = contentType === 'embed';
				let emoji: React.ReactNode | undefined;

				if (displayEmoji && !isSmartLinkEmbed) {
					emoji = (
						<LazyEmojiComponentLoader
							emoji={displayEmoji}
							context="pageTree"
							height={14}
							key={displayEmoji}
							wrapper={EmojiWrapper}
							renderResourcedEmoji={renderResourcedEmoji}
						/>
					);
				}
				return renderItem({
					...params,
					spaceHomePageId,
					onClick, //only needed at all if we're in the move page dialog
					//@ts-ignore
					updateSinglePage: actions.updateSinglePage,
					// this causes all items in a tree to re-render when a drag starts
					getIsDraggingItem,
					spaceId: space?.id,
					emoji,
					getContentTreeSize: actions.getCurrentTreeSize,
					isSmartFoldersEnabledForTree: isSmartFoldersEnabled,
					onMovePageAction,
				});
			},
			[
				space?.homepage?.id,
				space?.id,
				renderItem,
				onClick,
				actions.updateSinglePage,
				actions.getCurrentTreeSize,
				getIsDraggingItem,
				renderResourcedEmoji,
				isSmartFoldersEnabled,
				onMovePageAction,
			],
		);

		const onDragStartFn = useCallback(() => {
			onDragStart?.();
		}, [onDragStart]);

		if (state.error) {
			return (
				<OptionalMenuListItem
					asListItem={
						!isMovePageTree &&
						expValEquals('confluence_sidenav_content_list_semantics', 'isEnabled', true)
					}
				>
					<PageTreeErrorComponentLoader
						error={state.error.error}
						isExpected={state.error.isExpected}
						onRetry={handleRetry}
					/>
				</OptionalMenuListItem>
			);
		}

		const showFooterCreateButtons = !detached && spaceKey !== '' && !isMultiSelectActive;

		if (isPageTreeEmpty) {
			return (
				<OptionalMenuListItem
					asListItem={
						!isMovePageTree &&
						expValEquals('confluence_sidenav_content_list_semantics', 'isEnabled', true)
					}
				>
					<PageTreeEmptyComponentLoader
						rootId={rootId}
						spaceKey={spaceKey}
						isPageTreeLoading={isPageTreeLoading}
						showFooterCreateButtons={showFooterCreateButtons}
						onInitialLoadComplete={onInitialLoadComplete}
						actions={actions}
					/>
				</OptionalMenuListItem>
			);
		}

		return (
			<Fragment>
				<OptionalMenuListItem
					asListItem={
						!isMovePageTree &&
						expValEquals('confluence_sidenav_content_list_semantics', 'isEnabled', true)
					}
				>
					<PageTreeComponent
						isDraggable={isTreeDraggable}
						onDragBlocked={onDragBlocked}
						isTreeTruncatedPrevious={isTreeTruncatedPrevious}
						isTreeTruncatedFollowing={isTreeTruncatedFollowing}
						loadPreviousPages={loadPreviousPages}
						loadFollowingPages={loadFollowingPages}
						//@ts-ignore
						onCollapse={actions.collapsePage}
						onDragStart={onDragStartFn}
						onDragEnd={movePage}
						onExpand={expandPage}
						onInitialLoadComplete={onInitialLoadComplete}
						tree={memoizedTree}
						loading={isPageTreeLoading}
						renderItem={renderPageTreeItem}
						{...(!isMovePageTree && { renderItemEmptyState })}
						setIsDraggingItem={setIsDraggingItem}
						dragPreview={dragPreview}
					/>
				</OptionalMenuListItem>
				{showFooterCreateButtons && (
					<OptionalMenuListItem
						asListItem={
							!isMovePageTree &&
							expValEquals('confluence_sidenav_content_list_semantics', 'isEnabled', true)
						}
					>
						<FooterCreateButtons spaceKey={spaceKey} isContentTreeLoading={isPageTreeLoading} />
					</OptionalMenuListItem>
				)}
				{isMultiSelectActive && (
					<OptionalMenuListItem
						asListItem={
							!isMovePageTree &&
							expValEquals('confluence_sidenav_content_list_semantics', 'isEnabled', true)
						}
					>
						<BulkActionsMenu />
					</OptionalMenuListItem>
				)}
				<QuickReloadSubscriber
					name="pagetree"
					types={[ReloadType.content]}
					reload={reloadChildren as any}
				/>
				<PageTreeSmartLinkDropMonitor
					spaceKey={spaceKey}
					flags={flags}
					onInsert={(params) =>
						actions.optimisticInsertSmartLinkEmbed({
							...params,
							queryChildren,
							movePage: async (moveParams) => {
								if (params.position) {
									await movePageHandler(moveParams);
								}
							},
						})
					}
					onDelete={(contentId) => actions.optimisticDeleteContent(contentId, undefined)}
				/>
				{renderServerPlaceholder && <StopLinkClickSSRInlineScript />}
			</Fragment>
		);
	},
);

const useLegacyInitialData = (props: PageTreeCoordinatorProps) => {
	const { query, options } = generateQueryArgs(
		props.currentPageId,
		true,
		false,
		false,
		props.spaceKey,
		props.isSuperAdmin,
	);
	return useQueryInterop<PageTreeQueryResponse | PageTreeRootLevelPagesResponse>(query, {
		variables: options.variables,
		fetchPolicy: 'cache-only',
	}); //cacheOnly will load cached Data
};

export const PageTreeCoordinator: FC<PageTreeCoordinatorProps> = (props) => {
	const { initialDataFragmentRef } = props;

	return initialDataFragmentRef ? (
		<PageTreeCoordinatorFromFragment {...props} initialDataFragmentRef={initialDataFragmentRef} />
	) : (
		<LegacyPageTreeCoordinator {...props} />
	);
};

const LegacyPageTreeCoordinator: FC<PageTreeCoordinatorProps> = (props) => {
	const { data, error, refetch } = useLegacyInitialData(props);

	return <PageTreeCoordinatorInner {...props} initialData={data} error={error} refetch={refetch} />;
};

const PageTreeCoordinatorFromFragment: FC<
	PageTreeCoordinatorProps & { initialDataFragmentRef: PageTreeInitialData_query$key }
> = (props) => {
	const fragmentData = useFragment(PageTreeInitialDataFragment, props.initialDataFragmentRef);
	const { error, refetch } = useLegacyInitialData(props);
	const initialData = useMemo(
		() =>
			fragmentData.currentPage != null || fragmentData.rootPageTree != null
				? {
						currentPage: fragmentData.currentPage,
						ptpage: fragmentData.rootPageTree,
						space: fragmentData.space,
					}
				: {
						// Match preloadPageTree's space-key cache mapping for blog and overview routes.
						currentPage: fragmentData.space?.pageTree ?? null,
						ptpage: fragmentData.homepageRootPageTree,
						space: fragmentData.space,
					},
		[
			fragmentData.currentPage,
			fragmentData.homepageRootPageTree,
			fragmentData.rootPageTree,
			fragmentData.space,
		],
	);

	return (
		<PageTreeCoordinatorInner
			{...props}
			initialData={initialData}
			error={error}
			refetch={refetch}
		/>
	);
};

const PageTreeCoordinatorInner: FC<
	PageTreeCoordinatorProps & {
		initialData: unknown;
		error: ReturnType<typeof useLegacyInitialData>['error'];
		refetch: ReturnType<typeof useLegacyInitialData>['refetch'];
	}
> = ({ initialData, error, refetch, ...props }) => {
	const [{ contentId: selectedPageId }] = usePageState();
	const derrivedCurrentPage = props.detached ? props.currentPageId : selectedPageId;

	const handleRetry = useCallback(() => {
		if (refetch) {
			void refetch();
		}
	}, [refetch]);

	if (error) {
		return <PageTreeErrorComponentLoader error={error} onRetry={handleRetry} />;
	}

	return (
		<PageTreeStateContainer
			currentPageId={derrivedCurrentPage}
			// @ts-ignore
			highlightPages={props.highlightPages}
			initialData={initialData}
			spaceKey={props.spaceKey}
			syntheticItem={props.syntheticItem}
			onExpand={props.onExpand}
			onCollapse={props.onCollapse}
			onLoadError={props.onLoadError}
			syntheticItemIsDraft={props.syntheticItemIsDraft}
			isPeekingFromBlogs={props.isPeekingFromBlogs}
			detached={props.detached}
		>
			<PageTreeCoordinatorComponent
				currentPageId={props.currentPageId}
				isPeekingFromBlogs={props.isPeekingFromBlogs}
				isSuperAdmin={props.isSuperAdmin}
				spaceKey={props.spaceKey}
				statuses={props.statuses}
				setIsPageTreeLoading={props.setIsPageTreeLoading}
				expandSpaceHomepage={props.expandSpaceHomepage}
				onInitialLoadComplete={props.onInitialLoadComplete}
				onLoadMore={props.onLoadMore}
				onDragStart={props.onDragStart}
				onPageMove={props.onPageMove}
				onMoveInvalid={props.onMoveInvalid}
				renderItem={props.renderItem}
				isDraggable={props.isDraggable}
				syntheticItem={props.syntheticItem}
				detached={props.detached}
				syntheticItemIsDraft={props.syntheticItemIsDraft}
				forceFetchCurrentPage={props.forceFetchCurrentPage}
				forceFetchedCallback={props.forceFetchedCallback}
				optimisticDeleteId={props.optimisticDeleteId}
				clearDeleteId={props.clearDeleteId}
				highlightPages={props.highlightPages}
				getIsDraggingItem={props.getIsDraggingItem}
				setIsDraggingItem={props.setIsDraggingItem}
				setOpenQuickActionsId={props.setOpenQuickActionsId}
				dragPreview={props.dragPreview}
				renderResourcedEmoji={props.renderResourcedEmoji}
				setContentTreeSize={props.setContentTreeSize}
				setIsPageTreeEmpty={props.setIsPageTreeEmpty}
				selectedPageId={selectedPageId}
				isMovePageTree={props.isMovePageTree}
				moveToParentId={props.moveToParentId}
				pageTreeStateUpdatesContainer={props.pageTreeStateUpdatesContainer}
				isCreateContentRoute={props.isCreateContentRoute}
				flags={props.flags}
			/>
		</PageTreeStateContainer>
	);
};

export type PageTreeCoordinatorProps = {
	initialDataFragmentRef?: PageTreeInitialData_query$key;
	currentPageId?: string;
	isPeekingFromBlogs?: boolean;
	isSuperAdmin?: boolean;
	spaceKey: string;
	forceFetchedCallback?: (data?: any) => void;
	optimisticDeleteId?: string;
	clearDeleteId?: (data?: any) => void;
	setIsPageTreeLoading?: (isLoading: boolean) => void;
	statuses?: GraphQLPageStatus[];

	/**
	 * Controls whether space homepage should be expanded once loaded.
	 * Useful if `currentPageId` is an orphan, or if using `syntheticItem`.
	 */
	expandSpaceHomepage?: boolean;

	onInitialLoadComplete?(): void;
	onLoadMore?(count?: any, spaceId?: any, direction?: any): void;
	onDragStart?(): void;
	onPageMove(
		moveParams: MovePageParams,
		additionalParams: {
			contentStatus: string | null;
			movedPageTitle: string;
			spaceId?: string | null;
			sourceParent: ContentTreeItem;
			source: TreeSourcePosition;
			destination: TreeDestinationPosition;
			revertMove(): MovePageParams;
			instructionType?: string | null;
		},
	): void;
	onMoveInvalid?(options: {
		reason:
			| 'TREE_TOO_LARGE'
			| 'INVALID_MOVE_LOCATION'
			| 'INVALID_MOVE_LOCATION_DRAFT'
			| 'INVALID_MOVE_LOCATION_MANAGED_FOLDER';
	}): void;
	onLoadError?(
		error: Error,
		options?: {
			isExpected?: boolean;
		},
	): void;
	onCollapse?(
		page: ContentTreeItem,
		options: {
			spaceId?: string | null;
		},
	): void;
	onExpand?(
		page: ContentTreeItem,
		options: {
			spaceId?: string | null;
		},
	): void;
	renderItem(
		item: RenderItemParams<ContentTreeItem> & {
			spaceHomePageId: string | undefined;
			onClick: (page: ContentTreeItem) => void;
			updateSinglePage: (id: string, data: object) => void;
			getIsDraggingItem?: () => boolean;
			spaceId?: string | null;
			emoji?: ReactNode;
			getContentTreeSize: () => number;
			isSmartFoldersEnabledForTree?: boolean;
			onMovePageAction?: (
				contentId: string,
				action: 'up' | 'down' | 'out' | 'into' | 'top' | 'bottom',
			) => void;
		},
	): ReactNode;
	renderResourcedEmoji?: boolean;

	// NOTE: If passing in both a valid currentPageId and highlightPages,
	// currentPageId takes preference in terms of how we load (and focus) the
	// page tree, and only those highlightPages that are resulting /
	// present in the page tree's state will be visually highlighted.

	// Similarly, if passing in >1 highlightPages, this component will reload and query
	// for the first ID in the array (as all page tree queries accept only a
	// single content ID focal point), but will highlight all "highlightPages"
	// that are loaded into state as a result (e.g. root pages or parents/siblings
	// of the focal highlighted page).
	highlightPages?: string[];

	// forces us to requery network for information on the current page
	forceFetchCurrentPage?: boolean;
	isDraggable(
		item: ContentTreeItem,
		options: {
			space?: {
				id: string;
				operations: Array<{
					targetType: string;
					operation: string;
				}> | null;
			} | null;
		},
	): boolean;

	/**
	 * This item is a "synthetic" one in a sense that it might not belong
	 * to the page tree that is currently being displayed.
	 * If provided, it will be either displayed as the first item on the upper-most
	 * parent of the tree (if it did not belong to the original tree), or override the
	 * original tree item (if it did belong to the tree).
	 *
	 * IMPORTANT NOTE: performing a drag of another item to appear right after this synthetic one
	 * will most likely produce undesirable and unexpected results.
	 *
	 * Valid use-cases include:
	 *  - (existing) moving a page from another space via drag-n-drop;
	 *  - (hypothetical) displaying a duplicate item on the tree for, like, discovery purposes
	 *  - (hypothetical) displaying a truly synthetic item that is non-homogenous to other TreeItem entries (i.e. pages)
	 *  - maybe others 😁
	 *
	 * ## 🛑 Current drawbacks 🐞
	 * Using `currentPageId` in conjunction with `syntheticItem` might produce undesirable UX on large trees.
	 * For example, if you have 500 pages in a tree and the current page is at position 300, then the first 50 pages won't
	 * actually be returned by the API. With a `syntheticItem` the tree would take approximately the following shape:
	 *
	 * ```
	 * <Load more button>
	 * synthetic item
	 *    pages 50-299
	 *    current page (300)
	 *    pages 301-500
	 * <Load more button>
	 * ```
	 */
	syntheticItem?: ContentTreeItem;
	/*
	 * Prop that signifies if we are outside normal pageLayout like the move page dialog. Tells controller to ignore all chaching
	 */
	detached?: boolean;
	syntheticItemIsDraft?: boolean;
	getIsDraggingItem?: () => boolean;
	setIsDraggingItem?: (state: boolean) => void;
	setOpenQuickActionsId?: (id: string | null) => void;
	dragPreview?: DragPreview<ContentTreeItem>;
	setContentTreeSize?: (size: number) => void;
	setIsPageTreeEmpty?: (isEmpty: boolean) => void;
	/**
	 * The id of the content that the user is viewing the route of. This is passed into useLoadPagesCallback when folders is enabled.
	 * The page tree filters out unpublished drafts except the one that was just created. We want to know if an unpublished draft is
	 * the selected page to avoid filtering it out after folder creation, as folders do not redirect to a create content route.
	 */
	selectedPageId?: string;
	isMovePageTree?: boolean;
	/**
	 * Move-dialog only. Optimistically reparent syntheticItem under this page when loaded. Requires syntheticItem.
	 */
	moveToParentId?: string | null;
	pageTreeStateUpdatesContainer?: PageTreeStateUpdater;
	isCreateContentRoute?: boolean;
	flags?: FlagsStateContainer;
};

type PrevPropsRef = MutableRefObject<{
	previousPageId: PageTreeCoordinatorProps['currentPageId'];
	previousSpaceKey: PageTreeCoordinatorProps['spaceKey'];
	previousHighlightedPages: PageTreeCoordinatorProps['highlightPages'];
	previousForceFetchCurrentPage: boolean;
	loadingUseEffect: boolean;
}>;

const i18nReorder = defineMessages({
	reorderAnnouncement: {
		id: 'page-tree.keyboard-reorder.announcement',
		defaultMessage: '{title} moved to position {position} of {total}',
		description:
			'Screen reader announcement made after a page is reordered up or down among its siblings using the keyboard-accessible move actions.',
	},
	moveOutAnnouncement: {
		id: 'page-tree.keyboard-reorder.move-out-announcement',
		defaultMessage: '{title} moved out one level',
		description:
			'Screen reader announcement made after a page is moved out of its parent (outdented) to the same level as its parent, using the keyboard-accessible move actions.',
	},
	moveIntoAnnouncement: {
		id: 'page-tree.keyboard-reorder.move-into-announcement',
		defaultMessage: '{title} moved under {target}',
		description:
			'Screen reader announcement made after a page is moved to become a child (indented under) of the item above it, using the keyboard-accessible move actions. {target} is the title of the new parent item.',
	},
});
